/**
 * Apple Pay Backend Service
 *
 * Handles Apple Pay-specific API calls:
 * - Phase 2: Payment Request - Backend provides ApplePayPaymentRequest
 * - Phase 4: Merchant Validation - Backend validates with Apple servers via MFL
 *
 * Checkout uses the shared /ecommerce/order-checkout endpoint via apiService.checkoutOrder().
 * Apple Pay passes single_use_payment_token instead of payment_instrument_id.
 *
 * @see https://developer.apple.com/documentation/applepayontheweb/apple-pay-js-api
 */

import type {BackendApplePayPaymentRequest} from '../../../../../queries/paymentCard/types';
import * as applePayApi from '../../../../../transport/applePayApi';
import {getServiceTokenization} from '../../../../../transport/paymentCardApiV2';
import type {SingleUsePaymentToken} from '../../../../../transport/types/checkout';
import {logger} from '../../../../../utils/logger';

import {buildSingleUsePaymentToken} from './applePayTokenNormalizer';
import type {ApplePayMerchantSession, ApplePayPayment, ApplePayPaymentRequest} from './types';

// Re-export transport types
export type {
  ValidateMerchantRequest,
  ValidateMerchantResponse,
} from '../../../../../transport/applePayApi';

// ============================================
// NORMALIZER
// ============================================

/**
 * Normalize backend payment_request (snake_case) to Apple's format (camelCase)
 *
 * Backend returns MFL format with snake_case keys.
 * ApplePaySession requires camelCase keys per Apple's spec.
 */
function normalizePaymentRequest(
  backendRequest: BackendApplePayPaymentRequest,
): ApplePayPaymentRequest {
  return {
    countryCode: backendRequest.country_code,
    currencyCode: backendRequest.currency_code,
    merchantCapabilities:
      backendRequest.merchant_capabilities as ApplePayPaymentRequest['merchantCapabilities'],
    supportedNetworks:
      backendRequest.supported_networks as ApplePayPaymentRequest['supportedNetworks'],
    total: backendRequest.total,
    lineItems: backendRequest.line_items,
    requiredBillingContactFields:
      backendRequest.required_billing_contact_fields as ApplePayPaymentRequest['requiredBillingContactFields'],
    requiredShippingContactFields:
      backendRequest.required_shipping_contact_fields as ApplePayPaymentRequest['requiredShippingContactFields'],
    shippingMethods: backendRequest.shipping_methods,
    shippingType: backendRequest.shipping_type,
    applicationData: backendRequest.application_data,
  };
}

// ============================================
// SERVICE
// ============================================

/**
 * Apple Pay Service
 *
 * Provides methods for:
 * 1. Getting payment request configuration from backend (Phase 2)
 * 2. Merchant validation (calls backend → MFL → Apple servers) (Phase 4)
 * 3. Building single_use_payment_token for checkout
 *
 * Checkout is handled by apiService.checkoutOrder() with single_use_payment_token.
 */
class ApplePayService {
  private readonly LOG_TAG = 'ApplePayService';

  /**
   * Current Apple Pay service ID (set when getPaymentRequest is called)
   * Used for subsequent API calls in the same payment flow.
   */
  private currentServiceId: string | null = null;

  /**
   * Get current merchant domain from window
   */
  private getMerchantDomain(): string {
    return typeof window !== 'undefined' ? window.location.hostname : '';
  }

  /**
   * Get current service ID (must call getPaymentRequest first)
   */
  getServiceId(): string | null {
    return this.currentServiceId;
  }

  /**
   * Clear the current service ID (call after checkout completes or fails)
   */
  clearServiceId(): void {
    this.currentServiceId = null;
  }

  /**
   * Fetch ApplePayPaymentRequest from backend
   *
   * Phase 2: Backend provides complete payment request with cart totals.
   * Frontend normalizes snake_case → camelCase for ApplePaySession.
   *
   * @param serviceId - Apple Pay service ID from usePaymentServices
   * @param remainingBalanceCents - Optional override for total (used for gift card split payments)
   * @returns ApplePayPaymentRequest ready for ApplePaySession
   */
  async getPaymentRequest(
    serviceId: string,
    remainingBalanceCents?: number,
  ): Promise<ApplePayPaymentRequest> {
    logger.info(this.LOG_TAG, 'Fetching payment request from backend', {
      serviceId,
      remainingBalanceCents,
    });

    // Store serviceId for subsequent API calls in this payment flow
    this.currentServiceId = serviceId;

    try {
      const response = await getServiceTokenization(serviceId);

      if (response.service_type !== 'apple_pay') {
        throw new Error(`Expected apple_pay service, got ${response.service_type}`);
      }

      const paymentRequest = normalizePaymentRequest(response.fields.payment_request);

      // Override total amount if remaining balance provided (gift card split payment)
      if (remainingBalanceCents !== undefined) {
        const remainingBalanceDollars = (remainingBalanceCents / 100).toFixed(2);
        paymentRequest.total = {
          ...paymentRequest.total,
          amount: remainingBalanceDollars,
        };
        logger.info(this.LOG_TAG, 'Overriding total for gift card split payment', {
          originalAmount: response.fields.payment_request.total.amount,
          remainingAmount: remainingBalanceDollars,
        });
      }

      logger.info(this.LOG_TAG, 'Payment request received from backend', {
        merchantLabel: paymentRequest.total.label,
        amount: paymentRequest.total.amount,
        networks: paymentRequest.supportedNetworks,
      });

      return paymentRequest;
    } catch (error) {
      this.currentServiceId = null; // Clear on failure
      logger.error(this.LOG_TAG, 'Failed to fetch payment request', {
        error: error instanceof Error ? error.message : String(error),
        serviceId,
      });
      throw new Error(
        `Failed to fetch payment request: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  /**
   * Validate merchant with Apple Pay servers
   *
   * Phase 4: Backend validates with Apple Pay servers via MFL.
   *
   * Flow: Frontend → Backend → MFL → Apple Pay servers
   *                              ↑
   *                        MFL has merchant cert
   *
   * CRITICAL: This MUST go through backend - browser cannot call Apple directly
   * due to CORS and certificate requirements.
   *
   * Route: POST /me/payment-services/:serviceId/validate-merchant
   *
   * @param validationURL - URL from Apple's onvalidatemerchant event
   * @returns Merchant session object to pass to completeMerchantValidation()
   * @throws Error if serviceId not set (must call getPaymentRequest first)
   */
  async validateMerchant(validationURL: string): Promise<ApplePayMerchantSession> {
    const initiativeContext = this.getMerchantDomain();

    logger.info(this.LOG_TAG, 'Validating merchant with backend', {
      validationURL,
      initiativeContext,
    });

    try {
      const response = await applePayApi.validateMerchant({
        validationURL,
        initiativeContext,
      });

      if (!response?.merchantSession) {
        throw new Error('No merchant session in response');
      }

      logger.info(this.LOG_TAG, 'Merchant validation successful');
      return response.merchantSession;
    } catch (error) {
      logger.error(this.LOG_TAG, 'Merchant validation failed', {
        error: error instanceof Error ? error.message : String(error),
        validationURL,
        initiativeContext,
      });
      throw new Error(
        `Merchant validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  /**
   * Build single_use_payment_token for checkout
   *
   * Creates token format expected by middleware → MFL:
   * { payment_service_id, token: JSON.stringify(payment.token) }
   *
   * Use with apiService.checkoutOrder({ single_use_payment_token, browser_information }).
   * Note: browser_information IS required for iOS Apple Pay (device info for transaction processing).
   *
   * @param payment - Apple Pay payment from onpaymentauthorized event
   * @param paymentServiceId - Apple Pay service ID (use currentServiceId or pass explicitly)
   * @returns Token for checkout API
   */
  buildPaymentToken(payment: ApplePayPayment, paymentServiceId?: string): SingleUsePaymentToken {
    const serviceId = paymentServiceId ?? this.currentServiceId;

    if (!serviceId) {
      throw new Error('Service ID required. Call getPaymentRequest() first or pass explicitly.');
    }

    logger.info(this.LOG_TAG, 'Building payment token for checkout', {
      paymentServiceId: serviceId,
      network: payment.token.paymentMethod.network,
      displayName: payment.token.paymentMethod.displayName,
    });

    return buildSingleUsePaymentToken(payment, serviceId);
  }
}

// Singleton instance
export const applePayService = new ApplePayService();

export default applePayService;
